import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import {
  deriveImportStage,
  fetchImportStatus,
  getLocalImport,
  subscribeLocalImport,
} from '../../services/presentationUploadService';

/** Poll interval while an import runs (Realtime makes it faster when the tables publish changes). */
export const IMPORT_POLL_MS = 2500;

/**
 * Live status of a PowerPoint import: the template row, its latest template_import request and
 * this tab's local upload state, combined into one visible stage.
 *
 * Realtime: subscribes to the request rows of this template (ai_requests is in the realtime
 * publication) and to the template row (templates is not published today; the listener costs
 * nothing and starts working if it is added). Polling every IMPORT_POLL_MS is the fallback and
 * stops once the import is ready or failed.
 *
 * @param {string|null} templateId - Template id (null disables everything).
 * @param {{ pollMs?: number, client?: object, deck?: boolean }} [options] - Poll interval and client (tests); `deck`
 *   maps progress to the wave 2 deck steps (Reading slides, Rendering, Suggesting AI fields).
 * A template row that is gone once loaded (a quota rejection deletes it) is a terminal failure
 * with `removed: true`, so nothing keeps polling a row that will never come back.
 *
 * @returns {{ stage: string, message: string, error: string|null, needsFile: boolean, code: string|null, removed: boolean, template: object|null, request: object|null, loaded: boolean, loadError: string|null, refresh: () => Promise<void> }}
 */
export default function usePresentationImportStatus(templateId, { pollMs = IMPORT_POLL_MS, client = supabase, deck = false } = {}) {
  const [remote, setRemote] = useState({ template: null, request: null, loaded: false, loadError: null });
  const [local, setLocal] = useState(() => (templateId ? getLocalImport(templateId) : null));
  const [now, setNow] = useState(() => Date.now());
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!templateId) return;
    try {
      const next = await fetchImportStatus(templateId, { client });
      if (!mountedRef.current) return;
      setRemote({ template: next.template, request: next.request, loaded: true, loadError: null });
    } catch (error) {
      if (!mountedRef.current) return;
      setRemote((prev) => ({ ...prev, loaded: true, loadError: error?.message || 'Could not load the import status.' }));
    } finally {
      if (mountedRef.current) setNow(Date.now());
    }
  }, [templateId, client]);

  // Local upload state (this tab).
  useEffect(() => {
    if (!templateId) return undefined;
    setLocal(getLocalImport(templateId));
    return subscribeLocalImport(templateId, (state) => {
      setLocal(state ? { ...state } : null);
      refresh();
    });
  }, [templateId, refresh]);

  const derived = useMemo(
    () => deriveImportStage({ template: remote.template, request: remote.request, local, now, loaded: remote.loaded && !remote.loadError, deck }),
    [remote.template, remote.request, local, now, remote.loaded, remote.loadError, deck]
  );
  const terminal = remote.loaded && (derived.stage === 'ready' || derived.stage === 'failed');

  // First load + polling fallback until ready/failed.
  useEffect(() => {
    if (!templateId) return undefined;
    if (terminal) return undefined;
    let timer = null;
    let cancelled = false;
    const tick = async () => {
      await refresh();
      if (!cancelled) timer = setTimeout(tick, pollMs);
    };
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [templateId, terminal, pollMs, refresh]);

  // Realtime nudges.
  useEffect(() => {
    if (!templateId || terminal || typeof client?.channel !== 'function') return undefined;
    let channel = null;
    try {
      channel = client
        .channel(`template-import:${templateId}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'ai_requests', filter: `template_id=eq.${templateId}` }, () => refresh())
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'templates', filter: `id=eq.${templateId}` }, () => refresh())
        .subscribe();
    } catch {
      channel = null;
    }
    return () => {
      if (channel && typeof client.removeChannel === 'function') client.removeChannel(channel);
    };
  }, [templateId, terminal, client, refresh]);

  return { ...derived, template: remote.template, request: remote.request, loaded: remote.loaded, loadError: remote.loadError, refresh };
}
